/**
 * API client for Zen Habits backend.
 */

import axios from 'axios';
import { User, Habit, HabitWithStatus, Completion, NotificationFailure } from '../types';


const getCookie = (name: string) => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      // Does this cookie string begin with the name we want?
      if (trimmedCookie.startsWith(`${name}=`)) {
        cookieValue = decodeURIComponent(trimmedCookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};


const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
'X-CSRFToken': getCookie('csrftoken'),
  },
});

export interface CreateHabitData {
  name: string;
  frequency: 'daily' | 'weekly' | 'custom' | 'periodic';
  custom_days?: number[];
  period_days?: number | null;
  period_start_date?: string | null;
  notification_time?: string | null;
}

export const habitApi = {
  list: () => api.get<HabitWithStatus[]>('/habits'),

  get: (id: number) => api.get<HabitWithStatus>(`/habits/${id}`),

  create: (data: CreateHabitData) => api.post<Habit>('/habits', data),

  update: (id: number, data: Partial<CreateHabitData>) =>
    api.patch<Habit>(`/habits/${id}`, data),

  delete: (id: number) => api.delete(`/habits/${id}`),

  toggleComplete: (id: number, date?: string) =>
    api.post(`/habits/${id}/complete`, date ? { date } : {}),

  getHistory: (id: number) => api.get<Completion[]>(`/habits/${id}/history`),
};

export interface TimezoneOption {
  value: string;
  label: string;
}

export interface TimezoneGroup {
  group: string;
  zones: TimezoneOption[];
}

export interface CompletionStats {
  completed_today: number;
  total_habits: number;
  total_completions: number;
  current_streak: number;
  longest_streak: number;
}

export interface CompletionSummary {
  completions: Record<string, number>;
  stats: CompletionStats;
  start_date: string;
  end_date: string;
}

export const userApi = {
  getMe: () => api.get<User>('/auth/me'),

  updateProfile: (data: { timezone?: string; is_onboarded?: boolean }) =>
    api.patch<User>('/auth/me', data),

  getTimezones: () => api.get<TimezoneGroup[]>('/timezones'),
};

export const completionApi = {
  getSummary: () => api.get<CompletionSummary>('/completions/summary'),
};

export const notificationApi = {
  getFailures: () => api.get<NotificationFailure[]>('/failures'),

  dismissFailure: (id: number) => api.delete(`/failures/${id}`),
};

export default api;
